import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicShell from './PublicShell';
import Icon from '../../comps/Icon';
import DiscoverSearch from './DiscoverSearch';
import { listBusinesses } from '../../services/publicBusiness';
import { useI18n } from '../../i18n/I18nContext';
import '../../style/css/directory2026.css';

// ── Directory / discovery (/discover) ────────────────────────────────────────
// Real data from the PUBLIC `publicBusinessList` act (no auth). Each business:
// { slug, name, city, cover, categories }. cover is an image path ('' → placeholder).

// Sentinel for the "all categories" filter — locale-independent (categories come from the DB in
// Hebrew; this is the internal selected-value key, its LABEL is translated via t('directory.all')).
const ALL = '__ALL__';

// Only allow a same-origin /media image path as a card cover — never render an arbitrary
// (possibly legacy/polluted) DB string as an <img src>. Anything else → placeholder.
const safeCover = (c) => (typeof c === 'string' && /^\/media\/[\w./-]+\.(jpe?g|png|webp|gif|avif)$/i.test(c) ? c : '');

// Build "/b/<slug>?date=&time=&party=" carrying the current booking intent — only
// appending params that are actually set, so clean links when nothing is chosen.
function buildBusinessHref(slug, intent) {
    const params = new URLSearchParams();
    if (intent.date) params.set('date', intent.date);
    if (intent.time) params.set('time', intent.time);
    if (intent.party) params.set('party', intent.party);
    const qs = params.toString();
    return qs ? `/b/${slug}?${qs}` : `/b/${slug}`;
}

function BusinessCard({ b, index, intent, t }) {
    const cats = Array.isArray(b.categories) ? b.categories : [];
    const initial = (b.name || '?').trim().charAt(0) || '?';
    const cover = safeCover(b.cover);
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: Math.min(index * 0.04, 0.3), ease: [0.2, 0.7, 0.2, 1] }}
        >
            <Link to={buildBusinessHref(b.slug, intent)} className={`dir-card${b.boosted ? ' dir-card--boosted' : ''}`}>
                {b.boosted ? <span className="dir-card-boost" aria-label={t('directory.boosted_aria')}>{t('directory.boosted')}</span> : null}
                <div className="dir-card-photo">
                    {cover
                        ? <img src={cover} alt={b.name} loading="lazy" />
                        : <span className="dir-card-ph" aria-hidden="true">{initial}</span>}
                </div>
                <div className="dir-card-body">
                    <h3 className="dir-card-name">{b.name}</h3>
                    {b.city ? (
                        <p className="dir-card-sub">
                            <Icon name="store" size={14} />{b.city}
                        </p>
                    ) : null}
                    {cats.length > 0 && (
                        <div className="dir-card-chips">
                            {cats.map((c) => <span key={c} className="dir-card-chip">{c}</span>)}
                        </div>
                    )}
                    <div className="dir-card-foot">
                        <span className="dir-card-cta">{t('directory.card_cta')}</span>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}

export default function BusinessDirectory() {
    const { t } = useI18n();
    const [searchParams] = useSearchParams();
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState(ALL);

    // Booking intent + filter read from the URL (written by DiscoverSearch on submit).
    const cityParam = (searchParams.get('city') || '').trim();
    const timeParam = searchParams.get('time') || '';
    const dateParam = searchParams.get('date') || '';
    const partyParam = searchParams.get('party') || '';
    const modeParam = searchParams.get('mode') || '';
    // NORMALIZE before filtering/link-building — a hand-edited URL can carry invalid params
    // that bypass DiscoverSearch's guards; never forward junk into /b/:slug links.
    const intent = useMemo(() => {
        const p = parseInt(partyParam, 10);
        return {
            date: /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : '',
            time: /^([01]\d|2[0-3]):[0-5]\d$/.test(timeParam) ? timeParam : '',
            party: p >= 1 && p <= 20 ? String(p) : '',
        };
    }, [dateParam, timeParam, partyParam]);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(false);
            try {
                const res = await listBusinesses();
                if (!alive) return;
                if (res && res.ok) setBusinesses(Array.isArray(res.data) ? res.data : []);
                else { setBusinesses([]); setError(true); }
            } catch (e) {
                if (alive) { setBusinesses([]); setError(true); }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Distinct category names present across all fetched businesses.
    const categories = useMemo(() => {
        const set = new Set();
        businesses.forEach((b) => (b.categories || []).forEach((c) => { if (c) set.add(c); }));
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'he'));
    }, [businesses]);

    // Distinct non-empty cities across businesses — offered in the DiscoverSearch picker.
    const cities = useMemo(() => {
        const set = new Set();
        businesses.forEach((b) => { const c = (b.city || '').trim(); if (c) set.add(c); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'he'));
    }, [businesses]);

    // Seed the search bar from the current URL params.
    const fromParams = useMemo(
        () => ({ city: cityParam, mode: modeParam, time: timeParam, date: dateParam, party: partyParam }),
        [cityParam, modeParam, timeParam, dateParam, partyParam],
    );

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const cityFilter = cityParam.toLowerCase();
        return businesses.filter((b) => {
            const cats = b.categories || [];
            if (category !== ALL && !cats.includes(category)) return false;
            // City filter from the URL — case-insensitive exact match on b.city.
            if (cityFilter && (b.city || '').trim().toLowerCase() !== cityFilter) return false;
            if (q) {
                const hay = `${b.name || ''} ${b.city || ''} ${cats.join(' ')}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [businesses, query, category, cityParam]);

    const dirty = query || category !== ALL;
    const clear = () => { setQuery(''); setCategory(ALL); };

    return (
        <PublicShell>
            <div className="dir">
                {/* Bold, flat-pink header: brand + prominent search + login */}
                <header className="dir-hero">
                    <div className="dir-hero-top">
                        <span className="dir-brand">
                            <span className="dir-brand-mark" aria-hidden="true"><Icon name="utensils" size={18} /></span>
                            HOPpA
                        </span>
                        <Link to="/login" className="dir-login">{t('directory.login')}</Link>
                    </div>
                    <h1 className="dir-hero-title">{t('directory.hero_title')}</h1>
                    <p className="dir-hero-sub">{t('directory.hero_sub')}</p>
                    <DiscoverSearch cities={cities} initial={fromParams} />
                </header>

                {/* Title band */}
                <div className="dir-titleband">
                    <h2>{t('directory.all_businesses')}</h2>
                    <p>{t('directory.filter_hint')}</p>
                </div>

                {/* Dynamic category filter — hidden when there are no categories */}
                {categories.length > 0 && (
                    <div className="dir-pillrows">
                        <div className="dir-pillrow" role="group" aria-label={t('directory.category_group')}>
                            <button type="button" className="dir-pill"
                                aria-pressed={category === ALL} onClick={() => setCategory(ALL)}>{t('directory.all')}</button>
                            {categories.map((c) => (
                                <button key={c} type="button" className="dir-pill"
                                    aria-pressed={category === c} onClick={() => setCategory(c)}>{c}</button>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && !error && (
                    <div className="dir-meta">
                        <span className="dir-count"><b>{results.length}</b> {t('directory.count_found', { count: '' }).trim()}</span>
                        {dirty && <button type="button" className="dir-clear" onClick={clear}>{t('directory.clear_filter')}</button>}
                    </div>
                )}

                {loading ? (
                    <div className="dir-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="dir-card dir-card-skel" aria-hidden="true">
                                <div className="dir-card-photo" />
                                <div className="dir-card-body">
                                    <span className="skel-line skel-line-lg" />
                                    <span className="skel-line" />
                                </div>
                            </div>
                        ))}
                        <span className="dir-loading-label">{t('directory.loading')}</span>
                    </div>
                ) : error ? (
                    <div className="dir-grid">
                        <div className="dir-empty">
                            <div className="emoji" aria-hidden="true"><Icon name="info" size={30} /></div>
                            <h3>{t('directory.error_title')}</h3>
                            <p>{t('directory.error_sub')}</p>
                        </div>
                    </div>
                ) : (
                    <div className="dir-grid">
                        {results.length === 0 ? (
                            <div className="dir-empty">
                                <div className="emoji" aria-hidden="true"><Icon name="search" size={30} /></div>
                                <h3>{t('directory.empty_title')}</h3>
                                <p>{t('directory.empty_sub')}</p>
                                {dirty && <button type="button" onClick={clear}>{t('directory.clear_filter')}</button>}
                            </div>
                        ) : (
                            results.map((b, i) => <BusinessCard key={b.slug} b={b} index={i} intent={intent} t={t} />)
                        )}
                    </div>
                )}
            </div>
        </PublicShell>
    );
}
